import type { Control } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { restoreWhenLeftEmpty } from "~/utils/form-utils";
import {
  PARSER_OPTIONS,
  sharePointSyncDefaultValues,
  SITE_OPTIONS,
  type SharePointSyncForm,
} from "~/utils/sharepoint-sync-form";
import { Card, Collapse, Form, FormItem, NumberField, Select, Switch } from "~/components/ui";

type SchemaOption = { value: string; label: string };

type SharePointSyncConfigFormProps = {
  control: Control<SharePointSyncForm>;
  schemaOptions: SchemaOption[];
  schemasLoading: boolean;
  /** Schemas synced incrementally, which always skip what is already ingested. */
  incrementalSchemaIds?: ReadonlySet<string>;
};

export function SharePointSyncConfigForm({
  control,
  schemaOptions,
  schemasLoading,
  incrementalSchemaIds,
}: SharePointSyncConfigFormProps) {
  const { t } = useTranslation("admin");
  const limitEnabled = useWatch({ control, name: "limitEnabled" });
  const schemaId = useWatch({ control, name: "schemaId" });
  const incremental = schemaId != null && (incrementalSchemaIds?.has(schemaId) ?? false);

  const advancedItems = [
    {
      key: "advanced",
      label: t("uploadAdmin.advanced.title"),
      children: (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Controller
              name="skipExisting"
              control={control}
              render={({ field }) => (
                <Switch
                  size="small"
                  checked={incremental || field.value}
                  disabled={incremental}
                  onChange={field.onChange}
                  aria-label={t("uploadAdmin.advanced.skipExisting")}
                />
              )}
            />
            <span className="text-noa">{t("uploadAdmin.advanced.skipExisting")}</span>
            {incremental && (
              <span className="text-xs text-noa-muted">{t("uploadAdmin.advanced.skipExistingIncremental")}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Controller
              name="limitEnabled"
              control={control}
              render={({ field }) => (
                <Switch
                  size="small"
                  checked={field.value}
                  onChange={field.onChange}
                  aria-label={t("uploadAdmin.advanced.maxRecords")}
                />
              )}
            />
            <span className="text-noa">{t("uploadAdmin.advanced.maxRecords")}</span>
            {limitEnabled && (
              <Controller
                name="maxRecords"
                control={control}
                render={({ field }) => (
                  <NumberField
                    value={field.value}
                    // Emptying the field reports null, which the schema rejects even with the limit
                    // off: ignore it while typing and fall back to the default if left empty.
                    onChange={(value) => value !== null && field.onChange(value)}
                    onBlur={restoreWhenLeftEmpty(sharePointSyncDefaultValues.maxRecords, field.onChange, field.onBlur)}
                    min={1}
                    max={10000}
                    format={{ maximumFractionDigits: 0 }}
                    size="small"
                    className="w-20"
                  />
                )}
              />
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <Card title={t("uploadAdmin.sync.sharepoint.configuration")} size="small">
        <Form>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormItem
              label={t("uploadAdmin.sync.sharepoint.site")}
              tooltip={t("uploadAdmin.sync.sharepoint.siteHint")}
              className="mb-0"
            >
              <Controller
                name="site"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("uploadAdmin.sync.sharepoint.allSites")}
                    allowClear
                    options={SITE_OPTIONS}
                  />
                )}
              />
            </FormItem>

            <FormItem label={t("uploadAdmin.form.schema")} tooltip={t("uploadAdmin.form.schemaHint")} className="mb-0">
              <Controller
                name="schemaId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("uploadAdmin.form.schemaPlaceholder")}
                    allowClear
                    loading={schemasLoading}
                    options={schemaOptions}
                  />
                )}
              />
            </FormItem>

            <FormItem
              label={t("uploadAdmin.form.parser")}
              tooltip={t("uploadAdmin.sync.sharepoint.parserHint")}
              className="mb-0"
            >
              <Controller
                name="parser"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("uploadAdmin.form.parserPlaceholder")}
                    allowClear
                    options={[...PARSER_OPTIONS]}
                  />
                )}
              />
            </FormItem>
          </div>
        </Form>
      </Card>

      <Collapse items={advancedItems} size="small" />
    </>
  );
}
